import React, { useMemo, useState } from 'react';
import { Upload, X } from 'lucide-react';
import { ArtCategory } from '../types';
import { postsService } from '../services/postsService';
import { useLanguage } from '../context/LanguageContext';
import { ContentModerationError } from '../lib/contentModeration';
import { useAuth } from '../context/AuthContext';

interface CreatePostProps {
  onClose: () => void;
  onSubmit: (postData: Record<string, unknown>) => void;
}

const CreatePost: React.FC<CreatePostProps> = ({ onClose, onSubmit }) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '' as ArtCategory,
    tags: '',
    forSale: false,
    price: '',
    isPremium: false,
  });
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [mainFilePreviews, setMainFilePreviews] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const categoryLabels = useMemo<Record<ArtCategory, string>>(() => ({
    music: t('categories.music', 'Música'),
    writing: t('categories.writing', 'Escritura'),
    tattoo: t('categories.tattoo', 'Tatuajes'),
    digital_design: t('categories.digital_design', 'Diseño Digital'),
    fashion_design: t('categories.fashion_design', 'Diseño de Moda'),
    photography: t('categories.photography', 'Fotografía'),
    sculpture: t('categories.sculpture', 'Escultura'),
    painting: t('categories.painting', 'Pintura'),
    dance: t('categories.dance', 'Danza'),
    filmmaking: t('categories.filmmaking', 'Cine'),
    makeup: t('categories.makeup', 'Maquillaje'),
  }), [t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Combine cover image and main files
      const allFiles = [];
      if (coverImage) {
        allFiles.push(coverImage);
      }
      allFiles.push(...selectedFiles);

      const postData = {
        title: formData.title,
        description: formData.description,
        category: formData.category as ArtCategory,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        forSale: formData.forSale,
        price: formData.forSale ? parseFloat(formData.price) : undefined,
        isPremium: formData.isPremium,
        files: allFiles,
      };

      const newPost = await postsService.createPost(postData);
      onSubmit(postData);

      // Show success message
      setError('');

      // Close modal
      onClose();

      // Optional: Dispatch custom event to refresh posts in parent components
      window.dispatchEvent(new CustomEvent('postCreated', { detail: { post: newPost } }));
    } catch (err) {
      console.error('Error creating post:', err);
      if (err instanceof ContentModerationError) {
        const key = err.categories.includes('sexual')
          ? 'moderation.block.sexual'
          : err.categories.includes('profanity')
            ? 'moderation.block.profanity'
            : 'moderation.block.generic';
        setError(t(key, 'Your content violates our community guidelines.'));
      } else if (err instanceof Error) {
        if (err.message === 'PREMIUM_POST_LIMIT_REACHED') {
          setError(t('createPost.error.limit', 'Has alcanzado el límite de 3 publicaciones. Hazte premium para seguir compartiendo tu arte.'));
        } else if (err.message === 'Only artists can create posts.') {
          setError('Solo los artistas pueden crear publicaciones. Contacta con soporte si crees que deberías poder publicar.');
        } else if (err.message === 'User not authenticated') {
          setError('No estás autenticado. Por favor, inicia sesión de nuevo.');
        } else {
          setError(err.message || t('createPost.error.generic', 'Error al crear la publicación'));
        }
      } else {
        setError(t('createPost.error.generic', 'Error al crear la publicación'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const newFiles = [...selectedFiles, ...files];
    setSelectedFiles(newFiles);

    // Create previews for new files
    const newPreviews = [...mainFilePreviews];
    files.forEach((file) => {
      if (file.type.startsWith('image/')) {
        const url = URL.createObjectURL(file);
        newPreviews.push(url);
      } else {
        newPreviews.push(''); // No preview for non-images
      }
    });
    setMainFilePreviews(newPreviews);
  };

  const handleCoverChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate that it's an image
    if (!file.type.startsWith('image/')) {
      setError(t('createPost.coverInvalid', 'La portada debe ser una imagen (JPG, PNG, GIF)'));
      return;
    }

    setCoverImage(file);
    setError('');

    // Create preview URL
    const url = URL.createObjectURL(file);
    setCoverPreview(url);
  };

  const removeCover = () => {
    setCoverImage(null);
    if (coverPreview) {
      URL.revokeObjectURL(coverPreview);
      setCoverPreview(null);
    }
  };
  const removeFile = (index: number) => {
    // Revoke URL if it's a preview
    if (mainFilePreviews[index]) {
      URL.revokeObjectURL(mainFilePreviews[index]);
    }

    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setMainFilePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const needsCover = formData.category === 'music' || formData.category === 'writing';
  const getFileAcceptString = () => {
    if (formData.category === 'music') {
      return 'audio/*,.mp3,.wav,.flac,.ogg,.m4a';
    } else if (formData.category === 'writing') {
      return '.pdf,.epub,.txt,.doc,.docx';
    }
    return 'image/*,video/*,audio/*,.pdf,.epub';
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-0 md:p-4 z-50">
      <div className="bg-white w-full h-full md:rounded-2xl md:max-w-2xl md:w-full md:max-h-[90vh] md:h-auto overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">{t('createPost.title', 'Crear Publicación')}</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors touch-target"
            >
              <X size={24} />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('createPost.titleLabel', 'Título de la obra')}
              </label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base"
                placeholder={t('createPost.titlePlaceholder', 'Nombre de tu obra de arte')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('createPost.categoryLabel', 'Categoría')}
              </label>
              <select
                required
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value as ArtCategory })}
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base"
              >
                <option value="">{t('createPost.categoryPlaceholder', 'Selecciona una categoría')}</option>
                {Object.entries(categoryLabels).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('createPost.descriptionLabel', 'Descripción')}
              </label>
              <textarea
                required
                rows={4}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none text-base"
                placeholder={t('createPost.descriptionPlaceholder', 'Describe tu obra, técnica utilizada, inspiración...')}
              />
            </div>

            {/* Cover Image for Music and Writing */}
            {needsCover && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {formData.category === 'music'
                    ? t('createPost.coverLabelMusic', 'Portada del álbum/canción')
                    : t('createPost.coverLabelWriting', 'Portada del libro/obra')}
                </label>
                {coverPreview ? (
                  <div className="space-y-3">
                    <img
                      src={coverPreview}
                      alt={t('createPost.coverPreviewAlt', 'Vista previa de la portada')}
                      className="w-32 h-32 object-cover rounded-lg mx-auto"
                    />
                    <div className="flex justify-center gap-2">
                      <label className="cursor-pointer bg-purple-600 text-white px-3 py-1 rounded text-sm hover:bg-purple-700 transition-colors">
                        {t('createPost.coverChange', 'Cambiar portada')}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleCoverChange}
                          className="hidden"
                        />
                      </label>
                      <button
                        type="button"
                        onClick={removeCover}
                        className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 transition-colors"
                      >
                        {t('createPost.coverRemove', 'Quitar')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Mobile camera options for cover */}
                    <div className="grid grid-cols-2 gap-3 md:hidden">
                      {/* Camera for cover */}
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 text-center">
                        <input
                          type="file"
                          accept="image/*"
                          capture="environment"
                          onChange={handleCoverChange}
                          className="hidden"
                          id="cover-camera"
                        />
                        <label htmlFor="cover-camera" className="cursor-pointer block">
                          <div className="text-2xl mb-1">📸</div>
                          <p className="text-xs text-gray-600">{t('createPost.camera', 'Cámara')}</p>
                        </label>
                      </div>

                      {/* Gallery for cover */}
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 text-center">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleCoverChange}
                          className="hidden"
                          id="cover-gallery"
                        />
                        <label htmlFor="cover-gallery" className="cursor-pointer block">
                          <div className="text-2xl mb-1">🖼️</div>
                          <p className="text-xs text-gray-600">{t('createPost.gallery', 'Galería')}</p>
                        </label>
                      </div>
                    </div>

                    {/* Desktop cover upload */}
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-purple-400 transition-colors hidden md:block">
                      <label className="cursor-pointer">
                        <Upload className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                        <p className="text-gray-600">{t('createPost.coverUpload', 'Haz clic para subir la portada')}</p>
                        <p className="text-sm text-gray-500 mt-1">{t('createPost.coverHint', 'JPG, PNG, GIF (recomendado: 500x500px)')}</p>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleCoverChange}
                          className="hidden"
                        />
                      </label>
                    </div>
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  {formData.category === 'music'
                    ? t('createPost.coverDescriptionMusic', 'La portada representa tu álbum o canción y aparecerá en el reproductor')
                    : t('createPost.coverDescriptionWriting', 'La portada representa tu libro u obra escrita y aparecerá en el lector')
                  }
                </p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {needsCover
                  ? (formData.category === 'music'
                    ? t('createPost.mainFileLabelMusic', 'Archivo principal (audio)')
                    : t('createPost.mainFileLabelWriting', 'Archivo principal (documento)'))
                  : t('createPost.mainFileLabelDefault', 'Archivos multimedia')
                }
              </label>
              <div className="space-y-4">
                {/* Mobile-friendly upload buttons */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Camera button for mobile */}
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-purple-400 transition-colors">
                    <input
                      type="file"
                      multiple
                      accept={getFileAcceptString()}
                      capture="environment"
                      onChange={handleFileChange}
                      className="hidden"
                      id="camera-upload"
                    />
                    <label htmlFor="camera-upload" className="cursor-pointer block">
                      <div className="w-12 h-12 mx-auto mb-2 bg-purple-100 rounded-full flex items-center justify-center">
                        <span className="text-2xl">📸</span>
                      </div>
                      <p className="text-gray-600 font-medium text-sm">{t('createPost.takePhoto', 'Tomar Foto')}</p>
                      <p className="text-xs text-gray-500 mt-1">{t('createPost.useCamera', 'Usar cámara')}</p>
                    </label>
                  </div>

                  {/* Gallery button */}
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-purple-400 transition-colors">
                    <input
                      type="file"
                      multiple
                      accept={getFileAcceptString()}
                      onChange={handleFileChange}
                      className="hidden"
                      id="gallery-upload"
                    />
                    <label htmlFor="gallery-upload" className="cursor-pointer block">
                      <div className="w-12 h-12 mx-auto mb-2 bg-blue-100 rounded-full flex items-center justify-center">
                        <Upload className="h-6 w-6 text-blue-600" />
                      </div>
                      <p className="text-gray-600 font-medium text-sm">{t('createPost.uploadFile', 'Subir Archivo')}</p>
                      <p className="text-xs text-gray-500 mt-1">{t('createPost.fromGallery', 'Desde galería')}</p>
                    </label>
                  </div>
                </div>

                {/* Alternative: Classic upload area for desktop */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-purple-400 transition-colors md:block hidden">
                  <input
                    type="file"
                    multiple
                    accept={getFileAcceptString()}
                    onChange={handleFileChange}
                    className="hidden"
                    id="file-upload-desktop"
                  />
                  <label htmlFor="file-upload-desktop" className="cursor-pointer">
                    <Upload className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                    <p className="text-gray-600">
                      {needsCover
                        ? (formData.category === 'music'
                          ? t('createPost.desktopUploadMusic', 'Haz clic para subir archivos de audio')
                          : t('createPost.desktopUploadWriting', 'Haz clic para subir documentos'))
                        : t('createPost.desktopUploadDefault', 'Haz clic para subir archivos')
                      }
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      {formData.category === 'music'
                        ? 'MP3, WAV, FLAC, OGG'
                        : formData.category === 'writing'
                          ? 'PDF, EPUB, TXT, DOC, DOCX'
                          : t('createPost.desktopUploadTypesDefault', 'Imágenes, videos, audio, PDF, EPUB')
                      }
                    </p>
                  </label>
                </div>
              </div>

              {selectedFiles.length > 0 && (
                <div className="mt-4 space-y-2">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      {/* File preview */}
                      <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                        {mainFilePreviews[index] ? (
                          <img
                            src={mainFilePreviews[index]}
                            alt={file.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="text-gray-400 text-xs">
                            {file.type.startsWith('audio/') ? '🎵' :
                              file.type.startsWith('video/') ? '🎬' :
                                file.type.includes('pdf') ? '📄' : '📁'}
                          </div>
                        )}
                      </div>

                      {/* File info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-700 truncate">{file.name}</p>
                        <p className="text-xs text-gray-500">
                          {(file.size / 1024 / 1024).toFixed(1)} MB
                        </p>
                      </div>

                      {/* Remove button */}
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {needsCover && !coverImage && (
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-700">
                    💡 <strong>{t('createPost.tipLabel', 'Consejo')}:</strong>{' '}
                    {formData.category === 'music'
                      ? t('createPost.tipMusic', 'Sube una portada para que tu música se vea más profesional')
                      : t('createPost.tipWriting', 'Sube una portada para que tu obra se vea más profesional')}
                  </p>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('createPost.tagsLabel', 'Tags (separados por comas)')}
              </label>
              <input
                type="text"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base"
                placeholder={
                  formData.category === 'music'
                    ? t('createPost.tagsPlaceholderMusic', 'rock, instrumental, guitarra, melodico')
                    : formData.category === 'writing'
                      ? t('createPost.tagsPlaceholderWriting', 'ficcion, romance, fantasia, novela')
                      : t('createPost.tagsPlaceholderDefault', 'arte, moderno, abstracto, colorido')
                }
              />
            </div>

            {user && (
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="isPremium"
                  checked={formData.isPremium}
                  onChange={(e) => setFormData({ ...formData, isPremium: e.target.checked })}
                  className="mt-1 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <label htmlFor="isPremium" className="text-sm text-gray-700">
                  <span className="font-medium">
                    {t('createPost.premiumLabel', 'Marcar como contenido premium')}
                  </span>
                  <span className="block text-xs text-gray-500 mt-0.5">
                    {t(
                      'createPost.premiumDescription',
                      'Solo los suscriptores premium podrán ver este contenido (además de ti y el equipo de moderación).'
                    )}
                  </span>
                </label>
              </div>
            )}

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="forSale"
                checked={formData.forSale}
                onChange={(e) => setFormData({ ...formData, forSale: e.target.checked })}
                className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <label htmlFor="forSale" className="text-sm font-medium text-gray-700">
                {t('createPost.forSaleLabel', 'Esta obra está en venta')}
              </label>
            </div>

            {formData.forSale && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('createPost.priceLabel', 'Precio (EUR)')}
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-gray-400 font-medium">€</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    required={formData.forSale}
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="w-full pl-8 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base"
                    placeholder={t('createPost.pricePlaceholder', '0.00')}
                  />
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                disabled={loading}
                onClick={onClose}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 touch-target text-base font-medium"
              >
                {t('createPost.cancel', 'Cancelar')}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all disabled:opacity-50 touch-target text-base font-medium"
              >
                {loading ? t('createPost.publishing', 'Publicando...') : t('createPost.publish', 'Publicar')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreatePost;
